import type { SiteLocale } from './locale'

export const selectedWorkCopy: Record<
  SiteLocale,
  { eyebrow: string; title: string; intro: string; watch: string; demoBadge: string }
> = {
  en: {
    eyebrow: 'Selected work',
    title: 'Websites and automations we have shipped',
    intro: 'Anonymous demonstrations of a translation ordering platform, a dental landing page, a law-firm website, and an AI lead-enrichment workflow. Full videos live on their own pages.',
    watch: 'Watch the demonstration',
    demoBadge: 'Anonymised demonstration',
  },
  fr: {
    eyebrow: 'Travaux sélectionnés',
    title: 'Des sites et des automatisations déjà livrés',
    intro: 'Démonstrations anonymes d’une plateforme de commandes de traduction, d’une landing page dentaire, d’un site de cabinet d’avocats et d’un workflow d’enrichissement de leads. Les vidéos complètes sont sur leur page dédiée.',
    watch: 'Voir la démonstration',
    demoBadge: 'Démonstration anonymisée',
  },
  ar: {
    eyebrow: 'أعمال مختارة',
    title: 'مواقع وأتمتة أنجزناها',
    intro: 'عروض مجهولة لمنصة طلبات ترجمة، وصفحة هبوط لعيادة أسنان، وموقع لمكتب محاماة، ومسار إثراء للعملاء المحتملين. الفيديوهات الكاملة على صفحاتها الخاصة.',
    watch: 'شاهد العرض',
    demoBadge: 'عرض مجهول الهوية',
  },
}

export type ProjectDemoCopy = {
  title: string
  description: string
  h1: string
  cardTitle: string
  cardDescription: string
  badge: string
  summary: string
  disclosure: string
  caption: string
  showsTitle: string
  shows: string[]
  limitsTitle: string
  limits: string
  workflowTitle: string
  workflow: string
  chaptersTitle: string
  chapters: string[]
  relatedTitle: string
  cta: string
  posterAlt: string
}

export const projectDemoCopy: Record<
  'lead-enrichment' | 'dental-landing' | 'law-firm' | 'translation-platform',
  Record<SiteLocale, ProjectDemoCopy>
> = {
  'translation-platform': {
    en: {
      title: 'Translation ordering platform with WhatsApp AI | Forteuno Web',
      description: 'Demonstration of a full-stack translation ordering system: online orders, an admin panel, automated transcription, and a chatbot that quotes and takes orders on the website and WhatsApp.',
      h1: 'Take translation orders online, or from WhatsApp',
      cardTitle: 'Translation ordering platform',
      cardDescription: 'A full-stack ordering site, admin panel, transcription, and a chatbot that quotes and places orders.',
      badge: 'Anonymised demonstration',
      summary: 'We built a full ordering platform for a translation business that had been running for years on a manual process. Customers can submit jobs on the website. Staff manage those jobs from an admin panel. AI services sit on top of that stack: automated transcription, and a customer-support chatbot on the website and on WhatsApp. The assistant answers questions, gives pricing, and lets people place an order without going back through the old manual path.',
      disclosure: 'This demonstration shows the platform we shipped: ordering, administration, transcription and the assistant. It does not prove a named client result, a conversion rate, or volume. Visible names and branding in the recording are treated as demo footage of the system, not a public case study with metrics.',
      caption: 'Anonymised demonstration — online ordering, admin, transcription, and an assistant that can quote and take orders.',
      showsTitle: 'What the footage shows',
      shows: [
        'The public site where people can order translation work online',
        'An admin panel used to handle incoming customer orders',
        'Automated transcription as an AI service on the platform',
        'A customer-support chatbot on the website',
        'The same assistant on WhatsApp: pricing, answers, and placing an order',
      ],
      limitsTitle: 'What it does not prove',
      limits: 'It does not prove a booking volume, a named client result, or that every order now bypasses staff. The recording illustrates the system: ordering, operations, transcription and an assistant that can quote and take a job.',
      workflowTitle: 'How the system is organised',
      workflow: 'A customer can order from the website or start in WhatsApp. The assistant answers questions, shares pricing, and can create the order so the person does not have to repeat a manual request. Staff see and process orders in the admin panel. Jobs that need it go through automated transcription instead of a separate hand-typed step.',
      chaptersTitle: 'Chapter summary',
      chapters: [
        'Online ordering on the public site',
        'Admin panel for customer orders',
        'Automated transcription service',
        'Website and WhatsApp assistant: answers, pricing, order placement',
      ],
      relatedTitle: 'Related',
      cta: 'Discuss a similar ordering platform',
      posterAlt: 'Preview of the translation ordering platform demonstration',
    },
    fr: {
      title: 'Plateforme de commandes de traduction avec IA WhatsApp | Forteuno Web',
      description: 'Démonstration d’un système complet de commandes de traduction : commandes en ligne, panneau d’administration, transcription automatisée, et un chatbot qui donne les tarifs et prend les commandes sur le site et WhatsApp.',
      h1: 'Prendre les commandes de traduction en ligne, ou depuis WhatsApp',
      cardTitle: 'Plateforme de commandes de traduction',
      cardDescription: 'Un site de commandes, un panneau d’administration, la transcription, et un chatbot qui chiffre et enregistre les commandes.',
      badge: 'Démonstration anonymisée',
      summary: 'Nous avons construit une plateforme de commandes complète pour une entreprise de traduction qui travaillait encore avec un process manuel. Les clients passent leurs jobs sur le site. L’équipe les gère dans un panneau d’administration. Des services d’IA s’y ajoutent : transcription automatisée, et un chatbot d’assistance sur le site et sur WhatsApp. L’assistant répond aux questions, donne les tarifs, et permet de passer commande sans revenir au circuit manuel.',
      disclosure: 'Cette démonstration montre la plateforme livrée : commandes, administration, transcription et assistant. Elle ne prouve pas un résultat client nommé, un taux de conversion, ni un volume. Les noms et marques visibles dans l’enregistrement sont traités comme des images de démonstration du système, pas comme une étude de cas chiffrée.',
      caption: 'Démonstration anonymisée — commandes en ligne, administration, transcription, et un assistant qui peut chiffrer et prendre une commande.',
      showsTitle: 'Ce que la vidéo montre',
      shows: [
        'Le site public où l’on commande un travail de traduction',
        'Un panneau d’administration pour traiter les commandes clients',
        'La transcription automatisée comme service d’IA',
        'Un chatbot d’assistance sur le site',
        'Le même assistant sur WhatsApp : tarifs, réponses, et passage de commande',
      ],
      limitsTitle: 'Ce qu’elle ne prouve pas',
      limits: 'Elle ne prouve pas un volume de commandes, un résultat client nommé, ni que chaque commande contourne désormais l’équipe. L’enregistrement illustre le système : commandes, opérations, transcription, et un assistant capable de chiffrer et d’enregistrer un job.',
      workflowTitle: 'Comment le système est organisé',
      workflow: 'Un client peut commander sur le site ou commencer sur WhatsApp. L’assistant répond, donne un tarif, et peut créer la commande pour éviter de recommencer un process manuel. L’équipe voit et traite les commandes dans le panneau d’administration. Les jobs qui en ont besoin passent par la transcription automatisée plutôt que par une saisie séparée.',
      chaptersTitle: 'Résumé des chapitres',
      chapters: [
        'Commande en ligne sur le site public',
        'Panneau d’administration des commandes',
        'Service de transcription automatisée',
        'Assistant site et WhatsApp : réponses, tarifs, commande',
      ],
      relatedTitle: 'Liens utiles',
      cta: 'Discuter d’une plateforme de commandes similaire',
      posterAlt: 'Aperçu de la démonstration de la plateforme de commandes de traduction',
    },
    ar: {
      title: 'منصة طلبات ترجمة مع مساعد واتساب بالذكاء الاصطناعي | Forteuno Web',
      description: 'عرض لنظام متكامل لطلبات الترجمة: طلبات عبر الموقع، لوحة إدارة، تفريغ آلي، ومساعد يجيب ويعطي الأسعار ويسجّل الطلبات على الموقع وواتساب.',
      h1: 'استقبال طلبات الترجمة عبر الموقع أو من واتساب',
      cardTitle: 'منصة طلبات ترجمة',
      cardDescription: 'موقع طلبات ولوحة إدارة وتفريغ آلي ومساعد يسعّر ويسجّل الطلبات.',
      badge: 'عرض مجهول الهوية',
      summary: 'بنينا منصة طلبات كاملة لشركة ترجمة كانت تعتمد مسارا يدويا منذ سنوات. يضع العملاء الأعمال على الموقع. يديرها الفريق من لوحة إدارة. فوق هذا النظام خدمات ذكاء اصطناعي: تفريغ آلي، ومساعد دعم على الموقع وواتساب. يجيب المساعد على الأسئلة ويعطي الأسعار ويتيح تقديم الطلب دون العودة إلى المسار اليدوي.',
      disclosure: 'يوضح هذا العرض المنصة التي سلّمناها: الطلبات والإدارة والتفريغ والمساعد. لا يثبت نتيجة باسم عميل ولا نسبة تحويل ولا حجما. الأسماء والعلامات الظاهرة في التسجيل تُعامل كلقطات تجريبية للنظام، وليست دراسة حالة بأرقام.',
      caption: 'عرض مجهول الهوية — طلبات عبر الموقع، إدارة، تفريغ، ومساعد يمكنه التسعير وتسجيل الطلب.',
      showsTitle: 'ما يظهره الفيديو',
      shows: [
        'الموقع العام حيث يُطلب عمل الترجمة',
        'لوحة إدارة لمعالجة طلبات العملاء',
        'التفريغ الآلي كخدمة ذكاء اصطناعي',
        'مساعد دعم على الموقع',
        'المساعد نفسه على واتساب: أسعار وإجابات وتقديم طلب',
      ],
      limitsTitle: 'ما لا يثبته',
      limits: 'لا يثبت حجم طلبات، ولا نتيجة باسم عميل، ولا أن كل طلب يتجاوز الفريق. التسجيل يوضح النظام: الطلبات والتشغيل والتفريغ ومساعد يمكنه التسعير وتسجيل العمل.',
      workflowTitle: 'كيف يُنظَّم النظام',
      workflow: 'يمكن للعميل الطلب من الموقع أو البدء على واتساب. يجيب المساعد ويعطي سعرا ويمكنه إنشاء الطلب حتى لا يُعاد المسار اليدوي. يرى الفريق الطلبات ويعالجها في لوحة الإدارة. الأعمال التي تحتاج تفريغا تمر بالخدمة الآلية بدل خطوة كتابة منفصلة.',
      chaptersTitle: 'ملخص الفصول',
      chapters: [
        'الطلب عبر الموقع العام',
        'لوحة إدارة الطلبات',
        'خدمة التفريغ الآلي',
        'مساعد الموقع وواتساب: إجابات وأسعار وطلب',
      ],
      relatedTitle: 'روابط ذات صلة',
      cta: 'ناقش منصة طلبات مشابهة',
      posterAlt: 'معاينة عرض منصة طلبات الترجمة',
    },
  },
  'lead-enrichment': {
    en: {
      title: 'AI lead enrichment and personalisation pipeline | Forteuno Web',
      description: 'Demonstration of a workflow that cleans, enriches, segments and personalises an existing lead list from web and professional sources.',
      h1: 'Turn a raw list into enriched, usable prospects',
      cardTitle: 'AI lead enrichment',
      cardDescription: 'Clean, enrich and personalise an existing list — not net-new lead generation.',
      badge: 'Anonymised demonstration',
      summary: 'The workflow starts from an existing list. It checks the data, applies filters, enriches companies and profiles from the configured sources, then generates personalisation fields the sales team can use. Failures stay visible so incomplete records are not silently treated as reliable.',
      disclosure: 'This demonstration shows enrichment of existing leads. It should not be described as automatic generation of new prospects until a collection step is added and documented. Visible names, campaigns and URLs in the recording are treated as anonymous demo data.',
      caption: 'Anonymised demonstration — enrichment of an existing list, not new lead generation.',
      showsTitle: 'What the footage shows',
      shows: [
        'Import of an existing lead list',
        'Cleaning, validation, coverage and batches',
        'Filters and segments',
        'Website, LinkedIn, company and search enrichment',
        'AI personalisation fields in the enriched output',
        'Failure handling when a record cannot be completed',
      ],
      limitsTitle: 'What it does not prove',
      limits: 'It does not prove net-new lead generation, a conversion rate, or a named client result. The recording illustrates enrichment and personalisation of a list that already exists.',
      workflowTitle: 'How the workflow is organised',
      workflow: 'A list is imported, checked and filtered. Configured sources then add company and profile context. The model writes personalisation fields only from what those sources return. Records that fail a step remain marked as incomplete instead of being filled in by guesswork.',
      chaptersTitle: 'Chapter summary',
      chapters: [
        'Import and validation of the existing list',
        'Batch coverage, filters and segments',
        'Enrichment from web and professional sources',
        'Personalisation output and visible failures',
      ],
      relatedTitle: 'Related',
      cta: 'Discuss a similar workflow',
      posterAlt: 'Preview of the AI lead-enrichment workflow demonstration',
    },
    fr: {
      title: 'Pipeline IA d’enrichissement et personnalisation de leads | Forteuno Web',
      description: 'Démonstration d’un workflow qui nettoie, enrichit, segmente et personnalise des leads existants à partir de sources web et professionnelles.',
      h1: 'Transformer une liste brute en prospects enrichis et exploitables',
      cardTitle: 'Enrichissement de leads IA',
      cardDescription: 'Nettoyer, enrichir et personnaliser une liste existante — pas une génération de nouveaux prospects.',
      badge: 'Démonstration anonymisée',
      summary: 'Le workflow part d’une liste existante. Il vérifie les données, applique des filtres, enrichit les entreprises et profils à partir des sources configurées, puis génère des champs de personnalisation utilisables par l’équipe commerciale. Les échecs restent visibles afin que les données incomplètes ne soient pas silencieusement présentées comme fiables.',
      disclosure: 'Cette démonstration illustre l’enrichissement de leads. Elle ne doit pas être décrite comme une génération automatique de nouveaux prospects tant qu’une étape de collecte n’est pas ajoutée et documentée. Les noms, campagnes et URL visibles dans l’enregistrement sont traités comme des données de démonstration anonymes.',
      caption: 'Démonstration anonymisée — enrichissement d’une liste existante, pas une génération de nouveaux prospects.',
      showsTitle: 'Ce que la vidéo montre',
      shows: [
        'Import d’une liste de leads existante',
        'Nettoyage, validation, couverture et lots',
        'Filtres et segments',
        'Enrichissement site, LinkedIn, entreprise et recherche',
        'Champs de personnalisation IA dans le résultat enrichi',
        'Gestion des échecs lorsqu’une fiche ne peut pas être complétée',
      ],
      limitsTitle: 'Ce qu’elle ne prouve pas',
      limits: 'Elle ne prouve pas une génération nette de nouveaux prospects, un taux de conversion, ni un résultat client nommé. L’enregistrement illustre l’enrichissement et la personnalisation d’une liste déjà disponible.',
      workflowTitle: 'Comment le workflow est organisé',
      workflow: 'Une liste est importée, vérifiée et filtrée. Les sources configurées ajoutent ensuite le contexte entreprise et profil. Le modèle n’écrit des champs de personnalisation qu’à partir de ce que ces sources renvoient. Les fiches qui échouent à une étape restent marquées comme incomplètes au lieu d’être complétées par approximation.',
      chaptersTitle: 'Résumé des chapitres',
      chapters: [
        'Import et validation de la liste existante',
        'Couverture par lots, filtres et segments',
        'Enrichissement à partir des sources web et professionnelles',
        'Sortie de personnalisation et échecs visibles',
      ],
      relatedTitle: 'Liens utiles',
      cta: 'Discuter d’un workflow similaire',
      posterAlt: 'Aperçu de la démonstration du pipeline d’enrichissement de leads',
    },
    ar: {
      title: 'مسار إثراء وتخصيص العملاء المحتملين بالذكاء الاصطناعي | Forteuno Web',
      description: 'عرض لمسار ينظّف ويُثري ويقسّم ويخصّص قائمة عملاء محتملين موجودة انطلاقا من مصادر ويب ومهنية.',
      h1: 'تحويل قائمة خام إلى فرص مثرية وقابلة للاستخدام',
      cardTitle: 'إثراء العملاء المحتملين',
      cardDescription: 'تنظيف قائمة موجودة وإثراؤها وتخصيصها — وليست توليدا لعملاء جدد.',
      badge: 'عرض مجهول الهوية',
      summary: 'يبدأ المسار من قائمة موجودة. يتحقق من البيانات ويطبّق عوامل التصفية ويُثري الشركات والملفات من المصادر المضبوطة، ثم يولّد حقول تخصيص يمكن لفريق المبيعات استخدامها. تبقى الإخفاقات ظاهرة حتى لا تُعرض البيانات الناقصة على أنها موثوقة.',
      disclosure: 'يوضح هذا العرض إثراء عملاء محتملين موجودين. لا ينبغي وصفه بأنه توليد تلقائي لفرص جديدة ما لم تُضف خطوة جمع وتُوثَّق. الأسماء والحملات والروابط الظاهرة في التسجيل تُعامل كبيانات تجريبية مجهولة.',
      caption: 'عرض مجهول الهوية — إثراء قائمة موجودة، وليس توليدا لعملاء جدد.',
      showsTitle: 'ما يظهره الفيديو',
      shows: [
        'استيراد قائمة عملاء محتملين موجودة',
        'التنظيف والتحقق والتغطية والدفعات',
        'عوامل التصفية والشرائح',
        'إثراء من الموقع ولينكدإن والشركة والبحث',
        'حقول تخصيص بالذكاء الاصطناعي في الناتج المثري',
        'معالجة الإخفاق عندما يتعذر إكمال سجل',
      ],
      limitsTitle: 'ما لا يثبته',
      limits: 'لا يثبت توليدا صافيا لعملاء جدد، ولا نسبة تحويل، ولا نتيجة باسم عميل. التسجيل يوضح إثراء قائمة موجودة وتخصيصها.',
      workflowTitle: 'كيف يُنظَّم المسار',
      workflow: 'تُستورد قائمة ثم تُراجع وتُصفّى. تضيف المصادر المضبوطة سياق الشركة والملف. لا يكتب النموذج حقول التخصيص إلا مما تعيده تلك المصادر. السجلات التي تفشل في خطوة تبقى معلّمة كناقصة بدل ملئها بالتخمين.',
      chaptersTitle: 'ملخص الفصول',
      chapters: [
        'استيراد القائمة الموجودة والتحقق منها',
        'التغطية بالدفعات والتصفية والشرائح',
        'الإثراء من مصادر الويب والمصادر المهنية',
        'ناتج التخصيص والإخفاقات الظاهرة',
      ],
      relatedTitle: 'روابط ذات صلة',
      cta: 'ناقش مسارا مشابها',
      posterAlt: 'معاينة عرض مسار إثراء العملاء المحتملين',
    },
  },
  'dental-landing': {
    en: {
      title: 'Dental landing page with WhatsApp automation | Forteuno Web',
      description: 'Anonymous demonstration of a dental landing page with a form, contact capture and a WhatsApp automation path.',
      h1: 'Turn a care request into a qualified WhatsApp conversation',
      cardTitle: 'Dental landing page',
      cardDescription: 'A dental centre landing page with form, confirmation and a path toward WhatsApp.',
      badge: 'Anonymised demonstration',
      summary: 'This demonstration presents a landing page designed for a dental centre: offer clarification, visuals, calls to action, initial qualification and a handoff toward WhatsApp. Practice identity has been replaced to protect confidentiality.',
      disclosure: 'The current video shows the site, the form and the confirmation. It does not prove the full downstream WhatsApp automation until a sequence showing the form in the system, the first automated message and the conversation in the dashboard is added. Do not read a clinic or doctor name into this page.',
      caption: 'Anonymised demonstration — the landing page, form and confirmation. Not an end-to-end WhatsApp proof.',
      showsTitle: 'What the footage shows',
      shows: [
        'A landing page structured for a dental centre',
        'Offer, visuals and calls to action',
        'The contact form and confirmation',
        'The intended path from the page toward WhatsApp',
      ],
      limitsTitle: 'What it does not prove',
      limits: 'It does not prove the entire WhatsApp automation, a booking rate, or a named clinic result. The recording stops at the site, the form and the confirmation.',
      workflowTitle: 'What the page is built to do',
      workflow: 'A visitor understands the offer, leaves a request, and is meant to continue in WhatsApp. The page handles the first qualification. The assistant and dashboard sequence is a separate layer, shown elsewhere when that footage exists.',
      chaptersTitle: 'Chapter summary',
      chapters: [
        'Offer and visual structure of the landing page',
        'Call to action and qualification form',
        'Confirmation after the request is sent',
      ],
      relatedTitle: 'Related',
      cta: 'Discuss a dental website and WhatsApp path',
      posterAlt: 'Preview of the anonymised dental landing-page demonstration',
    },
    fr: {
      title: 'Landing page dentaire avec automatisation WhatsApp | Forteuno Web',
      description: 'Démonstration anonyme d’une landing page dentaire avec formulaire, prise de contact et parcours d’automatisation WhatsApp.',
      h1: 'Transformer une demande de soins en conversation WhatsApp qualifiée',
      cardTitle: 'Landing page dentaire',
      cardDescription: 'Une landing page de centre dentaire avec formulaire, confirmation et passage vers WhatsApp.',
      badge: 'Démonstration anonymisée',
      summary: 'Cette démonstration présente une landing page conçue pour un centre dentaire : clarification de l’offre, éléments visuels, appels à l’action, qualification initiale et passage vers WhatsApp. Les données et l’identité du cabinet ont été remplacées pour protéger la confidentialité.',
      disclosure: 'La vidéo actuelle montre le site, le formulaire et la confirmation. Elle ne prouve pas le parcours WhatsApp de bout en bout tant qu’une séquence montrant le formulaire reçu dans le système, le premier message automatisé et la conversation dans le tableau de bord n’est pas ajoutée. N’associez aucun nom de clinique ou de médecin à cette page.',
      caption: 'Démonstration anonymisée — le site, le formulaire et la confirmation. Pas une preuve WhatsApp de bout en bout.',
      showsTitle: 'Ce que la vidéo montre',
      shows: [
        'Une landing page structurée pour un centre dentaire',
        'L’offre, les visuels et les appels à l’action',
        'Le formulaire de contact et la confirmation',
        'Le passage prévu de la page vers WhatsApp',
      ],
      limitsTitle: 'Ce qu’elle ne prouve pas',
      limits: 'Elle ne prouve pas l’automatisation WhatsApp complète, un taux de rendez-vous, ni un résultat de clinique nommée. L’enregistrement s’arrête au site, au formulaire et à la confirmation.',
      workflowTitle: 'Ce que la page est conçue pour faire',
      workflow: 'Un visiteur comprend l’offre, laisse une demande, puis est censé continuer sur WhatsApp. La page gère la première qualification. La séquence assistant et tableau de bord est une couche séparée, montrée ailleurs lorsque cette vidéo existe.',
      chaptersTitle: 'Résumé des chapitres',
      chapters: [
        'Structure de l’offre et des visuels',
        'Appel à l’action et formulaire de qualification',
        'Confirmation après l’envoi de la demande',
      ],
      relatedTitle: 'Liens utiles',
      cta: 'Discuter d’un site dentaire et d’un parcours WhatsApp',
      posterAlt: 'Aperçu de la démonstration anonyme de landing page dentaire',
    },
    ar: {
      title: 'صفحة هبوط لعيادة أسنان مع أتمتة واتساب | Forteuno Web',
      description: 'عرض مجهول لصفحة هبوط لعيادة أسنان مع نموذج وتقاط اتصال ومسار أتمتة واتساب.',
      h1: 'تحويل طلب رعاية إلى محادثة واتساب مؤهلة',
      cardTitle: 'صفحة هبوط لعيادة أسنان',
      cardDescription: 'صفحة مركز أسنان مع نموذج وتأكيد ومسار نحو واتساب.',
      badge: 'عرض مجهول الهوية',
      summary: 'يعرض هذا العرض صفحة هبوط مصممة لمركز أسنان: توضيح العرض، العناصر البصرية، دعوات الإجراء، التأهيل الأولي والانتقال إلى واتساب. استُبدلت هوية العيادة لحماية السرية.',
      disclosure: 'يظهر الفيديو الحالي الموقع والنموذج والتأكيد. لا يثبت مسار واتساب كاملا إلى أن تُضاف لقطة تظهر النموذج في النظام وأول رسالة مؤتمتة والمحادثة في لوحة التحكم. لا تُرفق بهذه الصفحة أي اسم عيادة أو طبيب.',
      caption: 'عرض مجهول الهوية — الموقع والنموذج والتأكيد. ليست دليلا على واتساب من البداية إلى النهاية.',
      showsTitle: 'ما يظهره الفيديو',
      shows: [
        'صفحة هبوط منظّمة لمركز أسنان',
        'العرض والعناصر البصرية ودعوات الإجراء',
        'نموذج التواصل والتأكيد',
        'المسار المقصود من الصفحة نحو واتساب',
      ],
      limitsTitle: 'ما لا يثبته',
      limits: 'لا يثبت أتمتة واتساب الكاملة، ولا نسبة مواعيد، ولا نتيجة لعيادة مسماة. يتوقف التسجيل عند الموقع والنموذج والتأكيد.',
      workflowTitle: 'ما صُممت الصفحة لإنجازه',
      workflow: 'يفهم الزائر العرض ويترك طلبا ثم يُفترض أن يكمل على واتساب. الصفحة تتولى التأهيل الأول. تسلسل المساعد ولوحة التحكم طبقة منفصلة تُعرض في مكان آخر عندما يتوفر ذلك التسجيل.',
      chaptersTitle: 'ملخص الفصول',
      chapters: [
        'بنية العرض والعناصر البصرية',
        'دعوة الإجراء ونموذج التأهيل',
        'التأكيد بعد إرسال الطلب',
      ],
      relatedTitle: 'روابط ذات صلة',
      cta: 'ناقش موقعا لعيادة أسنان ومسار واتساب',
      posterAlt: 'معاينة العرض المجهول لصفحة هبوط عيادة الأسنان',
    },
  },
  'law-firm': {
    en: {
      title: 'Professional website example for a law firm | Forteuno Web',
      description: 'Anonymous demonstration of a website designed to structure practice areas and make professional contact straightforward.',
      h1: 'A clear, credible site to present a law firm',
      cardTitle: 'Law firm website',
      cardDescription: 'A professional site that structures practice areas and makes contact easy to find.',
      badge: 'Anonymised demonstration',
      summary: 'This video presents an example of a site built for the legal sector. Names, contact details and identifying elements stay anonymous. The page can explain structure, readability, trust and contact — it does not add an AI automation or a commercial result that is not in the footage.',
      disclosure: 'This is a generic website demonstration. Do not attach a personal or client name to this page. The recording shows the public site, not a case result or an automation behind the form.',
      caption: 'Anonymised demonstration of a professional law-firm website.',
      showsTitle: 'What the footage shows',
      shows: [
        'The public structure of a law-firm website',
        'How practice areas and the offer are presented',
        'How a visitor can find a professional contact path',
      ],
      limitsTitle: 'What it does not prove',
      limits: 'It does not prove an AI assistant, a conversion rate, or a named client engagement. Only what appears in the recording is claimed.',
      workflowTitle: 'What the site is built to do',
      workflow: 'A visitor should understand the firm’s areas of work quickly, trust the presentation, and know how to get in touch. The work is structure, readability and contact — not an unseen automation.',
      chaptersTitle: 'Chapter summary',
      chapters: [
        'Overall site structure and visual tone',
        'Practice areas and professional presentation',
        'Contact path',
      ],
      relatedTitle: 'Related',
      cta: 'Discuss a professional website',
      posterAlt: 'Preview of the anonymised law-firm website demonstration',
    },
    fr: {
      title: 'Exemple de site professionnel pour cabinet d’avocats | Forteuno Web',
      description: 'Démonstration anonyme d’un site conçu pour structurer les domaines d’intervention et faciliter une prise de contact professionnelle.',
      h1: 'Un site clair et crédible pour présenter un cabinet d’avocats',
      cardTitle: 'Site de cabinet d’avocats',
      cardDescription: 'Un site professionnel qui structure les domaines d’intervention et facilite la prise de contact.',
      badge: 'Démonstration anonymisée',
      summary: 'Cette vidéo présente un exemple de site réalisé pour le secteur juridique. Le nom, les coordonnées et les éléments identifiants doivent rester anonymes. La page peut expliquer le travail de structure, de lisibilité, de confiance et de prise de contact, mais ne doit pas ajouter une automatisation IA ou un résultat commercial qui n’apparaît pas dans les informations fournies.',
      disclosure: 'Il s’agit d’une démonstration générique de site. N’attachez aucun nom personnel ou client à cette page. L’enregistrement montre le site public, pas un résultat de dossier ni une automatisation derrière le formulaire.',
      caption: 'Démonstration anonymisée d’un site professionnel pour cabinet d’avocats.',
      showsTitle: 'Ce que la vidéo montre',
      shows: [
        'La structure publique d’un site de cabinet d’avocats',
        'La présentation des domaines d’intervention',
        'Le chemin vers une prise de contact professionnelle',
      ],
      limitsTitle: 'Ce qu’elle ne prouve pas',
      limits: 'Elle ne prouve pas un assistant IA, un taux de conversion, ni un engagement client nommé. Seul ce qui apparaît dans l’enregistrement est avancé.',
      workflowTitle: 'Ce que le site est conçu pour faire',
      workflow: 'Un visiteur doit comprendre rapidement les domaines du cabinet, faire confiance à la présentation et savoir comment prendre contact. Le travail porte sur la structure, la lisibilité et le contact — pas sur une automatisation invisible.',
      chaptersTitle: 'Résumé des chapitres',
      chapters: [
        'Structure générale et ton visuel',
        'Domaines d’intervention et présentation professionnelle',
        'Prise de contact',
      ],
      relatedTitle: 'Liens utiles',
      cta: 'Discuter d’un site professionnel',
      posterAlt: 'Aperçu de la démonstration anonyme du site de cabinet d’avocats',
    },
    ar: {
      title: 'مثال موقع مهني لمكتب محاماة | Forteuno Web',
      description: 'عرض مجهول لموقع صُمم لتنظيم مجالات التدخل وتسهيل تواصل مهني.',
      h1: 'موقع واضح وموثوق لتقديم مكتب محاماة',
      cardTitle: 'موقع مكتب محاماة',
      cardDescription: 'موقع مهني ينظّم مجالات العمل ويسهّل إيجاد طريقة التواصل.',
      badge: 'عرض مجهول الهوية',
      summary: 'يعرض هذا الفيديو مثالا لموقع أُنجز للقطاع القانوني. يبقى الاسم وبيانات التواصل والعناصر المعرِّفة مجهولة. يمكن للصفحة شرح بنية الموقع ووضوحه والثقة والتواصل، دون إضافة أتمتة بالذكاء الاصطناعي أو نتيجة تجارية لا تظهر في التسجيل.',
      disclosure: 'هذا عرض عام لموقع. لا تُرفق بهذه الصفحة أي اسم شخصي أو اسم عميل. يظهر التسجيل الموقع العام، لا نتيجة قضية ولا أتمتة خلف النموذج.',
      caption: 'عرض مجهول الهوية لموقع مهني لمكتب محاماة.',
      showsTitle: 'ما يظهره الفيديو',
      shows: [
        'البنية العامة لموقع مكتب محاماة',
        'كيفية عرض مجالات التدخل',
        'مسار التواصل المهني',
      ],
      limitsTitle: 'ما لا يثبته',
      limits: 'لا يثبت مساعدا بالذكاء الاصطناعي، ولا نسبة تحويل، ولا ارتباطا باسم عميل. يُذكر فقط ما يظهر في التسجيل.',
      workflowTitle: 'ما صُمم الموقع لإنجازه',
      workflow: 'ينبغي أن يفهم الزائر مجالات عمل المكتب بسرعة، وأن يثق في العرض، وأن يعرف كيف يتواصل. العمل هنا بنية ووضوح وتواصل — وليست أتمتة غير ظاهرة.',
      chaptersTitle: 'ملخص الفصول',
      chapters: [
        'البنية العامة والنبرة البصرية',
        'مجالات التدخل والعرض المهني',
        'مسار التواصل',
      ],
      relatedTitle: 'روابط ذات صلة',
      cta: 'ناقش موقعا مهنيا',
      posterAlt: 'معاينة العرض المجهول لموقع مكتب المحاماة',
    },
  },
}
